/**
 * SubmitDialog Component
 *
 * Entry dialog for both column guess and final answer.
 * Desktop (≥768px): 720×120 SVG with baked-in BACK/OK buttons.
 * Phone (<768px): 764×150 SVG with separate button SVG overlays.
 */

import React, { useState } from 'react';
import {
  ClueDialogBackground,
  ClueDialogBackgroundPhone,
  SolveDialogBackground,
  SolveDialogBackgroundPhone,
  EntryDialogBackButton,
  EntryDialogOkButton,
} from '../../svg';
import styles from './SubmitDialog.module.css';

const PHONE_BREAKPOINT = 768;

interface SubmitDialogProps {
  variant: 'column' | 'final';
  onSubmit: (guess: string) => void;
  onBack: () => void;
  /** Credit penalty for wrong column guess (2 or 3). Used in placeholder for column variant. */
  creditPenalty?: number;
}

const SubmitDialog: React.FC<SubmitDialogProps> = ({ variant, onSubmit, onBack, creditPenalty }) => {
  const [guess, setGuess] = useState('');
  const isPhone = typeof window !== 'undefined' && window.innerWidth < PHONE_BREAKPOINT;

  const handleSubmit = () => {
    if (guess.trim()) {
      onSubmit(guess.trim().toUpperCase());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onBack();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setGuess(e.target.value.toUpperCase());
  };

  const placeholder = variant === 'final'
    ? 'SOLVE THE PUZZLE'
    : creditPenalty != null
      ? `${creditPenalty} CREDITS AT STAKE`
      : 'ENTER YOUR GUESS';

  if (isPhone) {
    const BackgroundSvg = variant === 'column' ? ClueDialogBackgroundPhone : SolveDialogBackgroundPhone;
    return (
      <div className={styles.phoneContainer}>
        <BackgroundSvg className={styles.phoneBackground} />

        <textarea
          data-debug-dialog-input="true"
          value={guess}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={styles.phoneInput}
          autoFocus
          maxLength={80}
        />

        <button
          className={styles.phoneBackButton}
          onClick={onBack}
          aria-label="Back"
        >
          <EntryDialogBackButton style={{ width: '100%', height: '100%' }} />
        </button>

        <button
          className={styles.phoneOkButton}
          onClick={handleSubmit}
          aria-label="OK"
        >
          <EntryDialogOkButton style={{ width: '100%', height: '100%' }} />
        </button>
      </div>
    );
  }

  // Desktop layout — 720×120 SVG with baked-in button visuals
  const BackgroundSvg = variant === 'column' ? ClueDialogBackground : SolveDialogBackground;

  return (
    <div className={styles.dialogContainer}>
      <BackgroundSvg className={styles.background} />

      <textarea
        data-debug-dialog-input="true"
        value={guess}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={styles.input}
        autoFocus
        maxLength={80}
      />

      {/* Invisible click areas over baked-in button graphics */}
      <button
        className={styles.backButton}
        onClick={onBack}
        aria-label="Back"
        tabIndex={-1}
      />

      <button
        className={styles.okButton}
        onClick={handleSubmit}
        aria-label="OK"
        tabIndex={-1}
      />
    </div>
  );
};

export default SubmitDialog;
